import { client } from "./api/client.ts";
import { insightV2TemplateKeyValues } from "./api/types.ts";
import { navigateTo, setupTenantIdStorage } from "@sg-protect/core";
import { FeatureFlagKeys, type FireflyRuntime, type Route } from "@sg-protect/federation-runtime";
import {
    MixpanelLocation,
    MixpanelSublocation,
    getDefaultWorkspace,
    getGovernanceTenantId,
    requireFeatureFlagLoader
} from "@sg-protect/shell";
import { generatePath, redirect, replace } from "react-router";
import { FullScreenTenantAssessmentAuthenticatedLayout } from "./components/layout/FullScreenTenantAssessmentAuthenticatedLayout.tsx";
import { SetupRouteLayout } from "./components/layout/SetupRouteLayout.tsx";
import { ReviewCreationWizardLayout } from "./components/layout/ReviewCreationWizardLayout.tsx";
import { TenantAssessmentAuthenticatedLayout } from "./components/layout/TenantAssessmentAuthenticatedLayout.tsx";
import { NavigateToDefaultPage } from "./components/workspace/NavigateToDefaultPage.tsx";
import { getPaths, msalPaths, workspacePaths } from "./paths.ts";
import { base64UrlDecode, buildRemediationAdminConsentUrl } from "./utils/common.ts";
import { lazyRoute } from "./utils/lazyRoute.ts";
import { getReportSublocation } from "./utils/reportSublocation.ts";

/**
 * @deprecated We only keep it for a couple of weeks, until people have time to create new favorites.
 * We'll delete it on July 1st along with the msal routes
 *
 * Redirect users to the route with the workspace ID in the path.
 * @example "/app/overview" -> "/app/12345/overview"
 */
const redirectRoutes = (runtime: FireflyRuntime): Route[] =>
    Object.keys(msalPaths)
        // We have some rare cases where the path does not contain the workspace ID, and we need to make sure that we don't
        // try to re-add a route that already exists
        .filter(key => workspacePaths[key as keyof typeof workspacePaths].startsWith("/app/:workspaceId"))
        .map(key => ({
            path: msalPaths[key as keyof typeof msalPaths],
            element: null,
            async loader({ request, params }) {
                if (key in workspacePaths) {
                    const { data, response } = await client.GET("/bff/subscription/workspaces");
                    if (!data) {
                        throw new Response(null, { status: response.status, statusText: response.statusText });
                    }
                    const { workspaceId = "" } = params;
                    const defaultWorkspace = getDefaultWorkspace(data.workspaces, { workspaceId });
                    const pattern = workspacePaths[key as keyof typeof workspacePaths];
                    const url = new URL(request.url);
                    runtime.logger.warning(
                        `Route with missing workspace ID found: \n    ${msalPaths[key as keyof typeof msalPaths]}\nshould be:\n    ${pattern}`
                    ); // Log the redirection for debugging purposes
                    return replace(
                        generatePath(pattern, {
                            workspaceId: defaultWorkspace.id,
                            ...(params as Record<string, string>)
                        }) + url.search
                    );
                } else {
                    throw new Response(null, { status: 404 });
                }
            }
        }));

function getInsightsRoutes(): Route[] {
    const paths = getPaths();
    return [
        {
            path: paths.insights,
            lazy: () => import("./pages/insights.tsx"),
            handle: {
                trackingName: "Insights Page",
                pageTitle: "Insights",
                zeroStatePath: workspacePaths.insightsZeroState,
                restrictedStatePath: workspacePaths.insightsRestrictedState
            }
        },
        {
            path: paths.insightsZeroState,
            lazy: () => import("./pages/insights-zero-state.tsx"),
            handle: {
                trackingName: "Insights Get Started",
                pageTitle: "Get Started",
                isZeroState: true
            }
        },
        {
            path: paths.insightsRestrictedState,
            lazy: () => import("./components/RestrictedPage.tsx"),
            handle: {
                trackingName: "Insights Restricted",
                pageTitle: "Insights",
                isRestrictedState: true
            }
        },
        {
            path: paths.insightsDetail,
            lazy: () => import("./pages/insights.[insightId].tsx"),
            handle: {
                trackingName: "Insight Detailed Page",
                mixpanelLocation: MixpanelLocation.Insight,
                mixpanelSublocation: ({ params }) => insightV2TemplateKeyValues.find(k => k === params["insightId"]),
                pageTitle: "Insight Details",
                zeroStatePath: workspacePaths.insightsZeroState,
                restrictedStatePath: workspacePaths.insightsRestrictedState
            },
            children: [
                {
                    index: true,
                    loader: ({ request }) => {
                        const url = new URL(request.url);
                        const searchParams = url.search;

                        return redirect(`./object-list${searchParams}`);
                    },
                    shouldRevalidate: () => true
                },
                {
                    path: paths.insightsObjectList,
                    Component: lazyRoute(() => import("./pages/insights.[insightId].object-list.tsx")),
                    handle: {
                        trackingName: "Insight Object List Tab",
                        pageTitle: "Insight Object List"
                    }
                },
                {
                    path: paths.insightsPolicies,
                    Component: lazyRoute(() => import("./pages/insights.[insightId].policies.tsx")),
                    handle: {
                        trackingName: "Insight Policies Tab",
                        pageTitle: "Insight Policies"
                    }
                },
                {
                    path: paths.insightsSummary,
                    Component: lazyRoute(() => import("./pages/insights.[insightId].summary.tsx")),
                    handle: {
                        trackingName: "Insight Summary Tab",
                        pageTitle: "Insight Summary"
                    }
                }
            ]
        }
    ];
}

function getRecommendationsRoutes(): Route[] {
    const paths = getPaths();
    return [
        {
            path: paths.recommendations,
            lazy: () => import("./pages/recommendations.tsx"),
            handle: {
                trackingName: "Recommendations Page",
                pageTitle: "Recommendations"
            }
        }
    ];
}

const getSensitivityLabelsRoutes = (): Route[] => {
    const paths = getPaths();
    return [
        {
            path: paths.sensitivityLabels,
            lazy: () => import("./pages/sensitivity-labels.tsx"),
            handle: {
                trackingName: "Sensitivity Labels",
                pageTitle: "Sensitivity Labels",
                zeroStatePath: workspacePaths.homeZeroState,
                restrictedStatePath: workspacePaths.sensitivityLabelsRestrictedState
            }
        },
        {
            path: paths.sensitivityLabelsRestrictedState,
            lazy: () => import("./components/RestrictedPage.tsx"),
            handle: {
                trackingName: "Sensitivity Labels Restricted",
                pageTitle: "Sensitivity Labels",
                isRestrictedState: true
            }
        }
    ];
};

function getReportRoutes(runtime: FireflyRuntime): Route[] {
    const paths = getPaths();
    return [
        {
            path: paths.reports,
            lazy: () => import("./pages/reports.tsx"),
            handle: {
                trackingName: "Reports",
                mixpanelLocation: MixpanelLocation.Report,
                zeroStatePath: workspacePaths.reportsZeroState,
                restrictedStatePath: workspacePaths.reportsRestrictedState
            },
            children: [
                {
                    index: true,
                    lazy: () => import("./pages/reports/index.tsx"),
                    handle: {
                        trackingName: "Report Index",
                        pageTitle: "Reports"
                    }
                },
                {
                    path: paths.reportsView,
                    lazy: () => import("./pages/reports/view.tsx"),
                    handle: {
                        trackingName: "Report View",
                        mixpanelSublocation: ({ searchParams }) => getReportSublocation(searchParams),
                        pageTitle: "View Report"
                    }
                },
                {
                    path: paths.reportsCreate,
                    lazy: () => import("./pages/reports/create.tsx"),
                    handle: {
                        trackingName: "Report Create",
                        mixpanelSublocation: ({ searchParams }) =>
                            getReportSublocation(searchParams) ?? MixpanelSublocation.Manual,
                        pageTitle: "Create Custom Report"
                    }
                },
                {
                    path: paths.reportsGenerate,
                    loader: () => requireFeatureFlagLoader(runtime, "AiFeaturesEnabled", true),
                    lazy: () => import("./pages/reports/generate.tsx"),
                    handle: {
                        trackingName: "AI Report Generate",
                        pageTitle: "Generate AI Report"
                    }
                },
                {
                    path: paths.reportsUpdate,
                    lazy: () => import("./pages/reports/update.[reportId].tsx"),
                    handle: {
                        trackingName: "Report Update",
                        mixpanelSublocation: MixpanelSublocation.SavedReports,
                        pageTitle: "Update Custom Report"
                    }
                },
                {
                    path: paths.permissionsMatrix,
                    children: [
                        {
                            index: true,
                            element: null,
                            loader: ({ request, params }) => {
                                const url = new URL(request.url);
                                const { workspaceId = "" } = params;
                                return replace(
                                    generatePath(paths.permissionsMatrixSiteSelection, { workspaceId }) + url.search
                                );
                            }
                        },
                        {
                            path: paths.permissionsMatrixSiteSelection,
                            lazy: () => import("./pages/permissions-matrix-report/site-selection.tsx"),
                            handle: {
                                trackingName: "Permissions matrix report Site Selection",
                                pageTitle: "Select Sites for Permissions matrix report"
                            }
                        },
                        {
                            path: paths.permissionsMatrixReport,
                            lazy: () => import("./pages/permissions-matrix-report/report.tsx"),
                            handle: {
                                trackingName: "Permissions matrix report",
                                mixpanelSublocation: MixpanelSublocation.ContentCentric,
                                pageTitle: "Permissions matrix report"
                            }
                        }
                    ]
                },
                {
                    path: paths.userAccess,
                    loader: ({ params }) => {
                        const { workspaceId = "" } = params;
                        return runtime.getFeatureFlag(FeatureFlagKeys.UserCentricPmrReleased, false)
                            ? null
                            : replace(generatePath(paths.reports, { workspaceId }));
                    },
                    children: [
                        {
                            index: true,
                            element: null,
                            loader: ({ request, params }) => {
                                const url = new URL(request.url);
                                const { workspaceId = "" } = params;
                                return replace(generatePath(paths.userAccessSelection, { workspaceId }) + url.search);
                            }
                        },
                        {
                            path: paths.userAccessSelection,
                            lazy: () => import("./pages/user-access/selection.tsx"),
                            handle: {
                                trackingName: "User access report User Selection",
                                pageTitle: "Select Users for User access report"
                            }
                        },
                        {
                            path: paths.userAccessReport,
                            lazy: () => import("./pages/user-access/report.tsx"),
                            handle: {
                                trackingName: "User access report",
                                mixpanelSublocation: MixpanelSublocation.UserCentric,
                                pageTitle: "User access report"
                            }
                        }
                    ]
                },
                {
                    path: paths.groupAccess,
                    loader: ({ params }) => {
                        const { workspaceId = "" } = params;
                        return runtime.getFeatureFlag(FeatureFlagKeys.GroupCentricPmrReleased, false)
                            ? null
                            : replace(generatePath(paths.reports, { workspaceId }));
                    },
                    children: [
                        {
                            index: true,
                            element: null,
                            loader: ({ request, params }) => {
                                const url = new URL(request.url);
                                const { workspaceId = "" } = params;
                                return replace(generatePath(paths.groupAccessSelection, { workspaceId }) + url.search);
                            }
                        },
                        {
                            path: paths.groupAccessSelection,
                            lazy: () => import("./pages/group-access/selection.tsx"),
                            handle: {
                                trackingName: "Group access report Group Selection",
                                pageTitle: "Select Groups for Group access report"
                            }
                        },
                        {
                            path: paths.groupAccessReport,
                            lazy: () => import("./pages/group-access/report.tsx"),
                            handle: {
                                trackingName: "Group access report",
                                mixpanelSublocation: MixpanelSublocation.GroupCentric,
                                pageTitle: "Group access report"
                            }
                        }
                    ]
                },
                {
                    path: paths.versionStorageReport,
                    lazy: () => import("./pages/version-storage-report/report.tsx"),
                    handle: {
                        trackingName: "Version storage report",
                        mixpanelSublocation: MixpanelSublocation.Template,
                        pageTitle: "Version storage report"
                    }
                }
            ]
        },
        {
            path: paths.reportsZeroState,
            lazy: () => import("./pages/reports-zero-state.tsx"),
            handle: {
                trackingName: "Reports Get Started",
                pageTitle: "Get Started",
                isZeroState: true
            }
        },
        {
            path: paths.reportsRestrictedState,
            lazy: () => import("./components/RestrictedPage.tsx"),
            handle: {
                trackingName: "Reports Restricted",
                pageTitle: "Reports",
                isRestrictedState: true
            }
        }
    ];
}

const getAutomationRoutes = (): Route[] => {
    const paths = getPaths();
    return [
        {
            path: paths.automations,
            lazy: () => import("./pages/policies/index.tsx"),
            handle: {
                trackingName: "Policies Page",
                pageTitle: "Policies",
                zeroStatePath: workspacePaths.automationsZeroState,
                restrictedStatePath: workspacePaths.automationsRestrictedState
            }
        },
        {
            path: paths.automationsCreate,
            lazy: () => import("./pages/policies/create.tsx"),
            handle: {
                trackingName: "Policies Create",
                mixpanelLocation: MixpanelLocation.Policies,
                pageTitle: "Create Policy",
                zeroStatePath: workspacePaths.automationsZeroState,
                restrictedStatePath: workspacePaths.automationsRestrictedState
            }
        },
        {
            path: paths.automationsUpdate,
            lazy: () => import("./pages/policies/update.[automationId].tsx"),
            handle: {
                trackingName: "Policies Update",
                mixpanelLocation: MixpanelLocation.Policies,
                pageTitle: "Update Policy",
                zeroStatePath: workspacePaths.automationsZeroState,
                restrictedStatePath: workspacePaths.automationsRestrictedState
            }
        },
        {
            path: paths.automationsZeroState,
            lazy: () => import("./pages/policies/zero-state.tsx"),
            handle: {
                trackingName: "Policies Get Started",
                pageTitle: "Get Started",
                isZeroState: true
            }
        },
        {
            path: paths.automationsRestrictedState,
            lazy: () => import("./components/RestrictedPage.tsx"),
            handle: {
                trackingName: "Policies Restricted",
                pageTitle: "Policies",
                isRestrictedState: true
            }
        }
    ];
};

const getReviewsRoutes = (): Route[] => {
    const paths = getPaths();
    return [
        {
            handle: {
                zeroStatePath: workspacePaths.reviewsZeroState,
                restrictedStatePath: workspacePaths.reviewsRestrictedState
            },
            children: [
                {
                    path: paths.reviews,
                    lazy: () => import("./pages/reviews/index.tsx"),
                    handle: {
                        trackingName: "Reviews Page",
                        pageTitle: "Reviews"
                    }
                },
                {
                    path: paths.reviewsDetail,
                    lazy: () => import("./pages/reviews/[reviewId].tsx"),
                    handle: {
                        trackingName: "Reviews Detail",
                        pageTitle: "Review Details"
                    }
                }
            ]
        },
        {
            path: paths.reviewsZeroState,
            lazy: () => import("./pages/reviews/zero-state.tsx"),
            handle: {
                trackingName: "Reviews Get Started",
                pageTitle: "Get Started",
                isZeroState: true
            }
        },
        {
            path: paths.reviewsRestrictedState,
            lazy: () => import("./components/RestrictedPage.tsx"),
            handle: {
                trackingName: "Reviews Restricted",
                pageTitle: "Reviews",
                isRestrictedState: true
            }
        }
    ];
};

function getWorkspaceDetailsRoutes(): Route[] {
    const paths = getPaths();
    return [
        {
            handle: {
                restrictedStatePath: workspacePaths.workspaceDetailsRestrictedState
            },
            children: [
                {
                    path: paths.workspaceDetails,
                    lazy: () => import("./pages/workspace-details.[siteCollectionId].tsx"),
                    handle: {
                        trackingName: "Workspace Details",
                        mixpanelLocation: MixpanelLocation.WorkspaceDetails,
                        pageTitle: "Workspace Details"
                    },
                    children: [
                        {
                            path: paths.workspaceDetailsSharingLinks,
                            Component: lazyRoute(
                                () => import("./pages/workspace-details.[siteCollectionId].sharing-links.tsx")
                            ),
                            handle: {
                                trackingName: "Workspace Details Sharing Links Tab",
                                mixpanelSublocation: MixpanelSublocation.SharingLinks,
                                pageTitle: "Sharing Links"
                            }
                        },
                        {
                            path: paths.workspaceDetailsLibraries,
                            Component: lazyRoute(
                                () => import("./pages/workspace-details.[siteCollectionId].libraries.tsx")
                            ),
                            handle: {
                                trackingName: "Workspace Details Libraries Tab",
                                mixpanelSublocation: MixpanelSublocation.Libraries,
                                pageTitle: "Libraries"
                            }
                        },
                        {
                            path: paths.workspaceDetailsOverview,
                            Component: lazyRoute(
                                () => import("./pages/workspace-details.[siteCollectionId].overview.tsx")
                            ),
                            handle: {
                                trackingName: "Workspace Details Overview Tab",
                                mixpanelSublocation: MixpanelSublocation.Overview,
                                pageTitle: "Overview"
                            }
                        },
                        {
                            path: paths.workspaceDetailsDetails,
                            loader: ({ request }) => {
                                const searchParams = new URL(request.url).search;

                                return redirect(`../overview${searchParams}`);
                            },
                            shouldRevalidate: () => true
                        },
                        {
                            path: paths.workspaceDetailsMembership,
                            Component: lazyRoute(
                                () => import("./pages/workspace-details.[siteCollectionId].membership.tsx")
                            ),
                            handle: {
                                trackingName: "Workspace Details Membership Tab",
                                mixpanelSublocation: MixpanelSublocation.GroupMembership,
                                pageTitle: "Group membership"
                            }
                        },
                        {
                            path: paths.workspaceDetailsPermissions,
                            Component: lazyRoute(
                                () => import("./pages/workspace-details.[siteCollectionId].permissions.tsx")
                            ),
                            handle: {
                                trackingName: "Workspace Details Permissions Tab",
                                mixpanelSublocation: MixpanelSublocation.SitePermissions,
                                pageTitle: "Site permissions"
                            }
                        },
                        {
                            path: paths.workspaceDetailsActivityLog,
                            Component: lazyRoute(
                                () => import("./pages/workspace-details.[siteCollectionId].activity-log.tsx")
                            ),
                            handle: {
                                trackingName: "Workspace Details Activity Log Tab",
                                mixpanelSublocation: MixpanelSublocation.ActivityLog,
                                pageTitle: "Activity log"
                            }
                        }
                    ]
                }
            ]
        },
        {
            path: paths.workspaceDetailsRestrictedState,
            lazy: () => import("./components/RestrictedPage.tsx"),
            handle: {
                trackingName: "Workspace Details Restricted",
                pageTitle: "Workspace Details",
                isRestrictedState: true
            }
        }
    ];
}

function getUserDetailsRoutes(): Route[] {
    const paths = getPaths();
    return [
        {
            path: paths.userDetails,
            lazy: () => import("./pages/user-details.[userId].tsx"),
            handle: {
                trackingName: "User Details",
                mixpanelLocation: MixpanelLocation.UserDetails,
                pageTitle: "User Details"
            },
            children: [
                {
                    index: true,
                    loader: ({ request }) => {
                        const url = new URL(request.url);
                        const searchParams = url.search;
                        return redirect(`./overview${searchParams}`);
                    },
                    shouldRevalidate: () => true
                },
                {
                    path: paths.userDetailsOverview,
                    Component: lazyRoute(() => import("./pages/user-details.[userId].overview.tsx")),
                    handle: {
                        trackingName: "User Details Overview Tab",
                        mixpanelSublocation: MixpanelSublocation.Overview,
                        pageTitle: "Overview"
                    }
                },
                {
                    path: paths.userDetailsLicenses,
                    Component: lazyRoute(() => import("./pages/user-details.[userId].licenses.tsx")),
                    handle: {
                        trackingName: "User Details Licenses Tab",
                        mixpanelSublocation: MixpanelSublocation.Licenses,
                        pageTitle: "Licenses"
                    }
                },
                {
                    path: paths.userDetailsSharingLinks,
                    Component: lazyRoute(() => import("./pages/user-details.[userId].sharing-links.tsx")),
                    handle: {
                        trackingName: "User Details Shared Links Tab",
                        mixpanelSublocation: MixpanelSublocation.SharedLinks,
                        pageTitle: "Shared links"
                    }
                },
                {
                    path: paths.userDetailsMembership,
                    Component: lazyRoute(() => import("./pages/user-details.[userId].membership.tsx")),
                    handle: {
                        trackingName: "User Details Membership Tab",
                        mixpanelSublocation: MixpanelSublocation.Groups,
                        pageTitle: "Groups"
                    }
                },
                {
                    path: paths.userDetailsPermissions,
                    Component: lazyRoute(() => import("./pages/user-details.[userId].permissions.tsx")),
                    handle: {
                        trackingName: "User Details Permissions Tab",
                        mixpanelSublocation: MixpanelSublocation.UserPermissions,
                        pageTitle: "Permissions"
                    }
                }
            ]
        }
    ];
}

const getActivityLogRoutes = (): Route[] => {
    const paths = getPaths();
    return [
        {
            path: paths.activityLog,
            lazy: () => import("./pages/activity-log.tsx"),
            shouldRevalidate: () => true,
            handle: {
                trackingName: "Activity Log Page",
                pageTitle: "Activity Log",
                zeroStatePath: workspacePaths.activityLogZeroState,
                restrictedStatePath: workspacePaths.activityLogRestrictedState
            }
        },
        {
            path: paths.activityLogZeroState,
            lazy: () => import("./pages/activity-log-zero-state.tsx"),
            handle: {
                trackingName: "Activity Log Get Started",
                pageTitle: "Get Started",
                isZeroState: true
            }
        },
        {
            path: paths.activityLogRestrictedState,
            lazy: () => import("./components/RestrictedPage.tsx"),
            handle: {
                trackingName: "Activity Log Restricted",
                pageTitle: "Activity Log",
                isRestrictedState: true
            }
        }
    ];
};

const getOverviewRoutes = (): Route[] => {
    const paths = getPaths();
    return [
        {
            path: paths.home,
            lazy: () => import("./pages/overview.tsx"),
            handle: {
                trackingName: "Home Page",
                pageTitle: "Home",
                zeroStatePath: workspacePaths.homeZeroState,
                restrictedStatePath: workspacePaths.homeRestrictedState
            }
        },
        {
            path: paths.overview,
            element: null,
            loader({ params }) {
                const { workspaceId = "" } = params;

                return replace(generatePath(paths.home, { workspaceId }));
            },
            handle: {
                trackingName: "Overview Page Redirect"
            }
        },

        {
            path: paths.tenantInfo,
            lazy: () => import("./pages/tenant-info.tsx"),
            handle: {
                trackingName: "Tenant info Page",
                mixpanelLocation: MixpanelLocation.TenantInfo,
                pageTitle: "Tenant Info",
                zeroStatePath: workspacePaths.tenantInfoZeroState,
                restrictedStatePath: workspacePaths.tenantInfoRestrictedState
            }
        },
        {
            path: paths.tenantInfoZeroState,
            lazy: () => import("./pages/tenant-info-zero-state.tsx"),
            handle: {
                trackingName: "Tenant Info Get Started",
                pageTitle: "Get Started",
                isZeroState: true
            }
        },
        {
            path: paths.tenantInfoRestrictedState,
            lazy: () => import("./components/RestrictedPage.tsx"),
            handle: {
                trackingName: "Tenant Info Restricted",
                pageTitle: "Tenant info",
                isRestrictedState: true
            }
        }
    ];
};

const getRemediationConsent = (): Route[] => {
    const paths = getPaths();
    return [
        {
            path: paths.remediationConsent,
            loader: ({ request, params }) => {
                const { workspaceId = "" } = params;
                // During setup, getGovernanceTenantId() may not be available yet, so fall back to the setup tenant id.
                const tenantId = getGovernanceTenantId() ?? setupTenantIdStorage.get() ?? undefined;

                if (!tenantId) {
                    return redirect(generatePath(paths.tenantAssessment, { workspaceId }));
                }

                const url = new URL(request.url);
                const providedRedirectUri = url.searchParams.get("redirectUri");
                const redirectUri = providedRedirectUri
                    ? decodeURIComponent(providedRedirectUri)
                    : generatePath(paths.tenantAssessment, { workspaceId });

                navigateTo(buildRemediationAdminConsentUrl(tenantId, `${window.location.origin}${redirectUri}`));

                // The browser is leaving the app, so this route must never render nor enter history: keep the
                // navigation pending until the page unloads. Under MSW the target is an in-app stand-in, and only a
                // full load reboots the app the way the real trip to Microsoft does.
                return new Promise<never>(() => {});
            },
            handle: {
                trackingName: "Remediation Consent"
            }
        }
    ];
};

const getPostConsentRoutes = (): Route[] => {
    const paths = getPaths();
    return [
        {
            path: paths.remediationPostConsent,
            loader: ({ request }) => {
                const url = new URL(request.url);
                const params = url.searchParams;

                const state = params.get("state");
                if (!state) {
                    return redirect(msalPaths.tenantAssessment);
                }

                const { redirectUri } = base64UrlDecode(state);
                if (!redirectUri) {
                    return redirect(msalPaths.tenantAssessment);
                }

                const postConsentUri = new URL(redirectUri);
                url.searchParams.forEach((value, key) => postConsentUri.searchParams.append(key, value));

                return redirect(postConsentUri.toString());
            },
            handle: {
                trackingName: "Post Remediation Consent Redirect"
            }
        }
    ];
};

export const registerPublicRoutes = (): Route[] => {
    const paths = getPaths();
    return [
        {
            path: paths.tenantAssessmentConsented,
            lazy: () => import("./pages/tenant-assessment/consented.tsx"),
            $visibility: "public",
            handle: {
                trackingName: "Tenant Assessment Consented"
            }
        },
        {
            path: paths.setupRequestReviewPermission,
            lazy: () => import("./pages/setup/request-review-permissions.tsx"),
            $visibility: "public",
            handle: {
                trackingName: "Setup Request Review Permissions",
                pageTitle: "Review Permissions",
                isOnboardingStep: true
            }
        },
        {
            path: paths.setupRequestConnected,
            lazy: () => import("./pages/setup/request-connected.tsx"),
            $visibility: "public",
            handle: {
                trackingName: "Setup Request Connected",
                pageTitle: "Connected",
                isOnboardingStep: true
            }
        },
        {
            path: paths.workspaceTenantReconsentRequest,
            lazy: () => import("./pages/workspace-tenant-reconsent-request.tsx"),
            $visibility: "public",
            handle: {
                trackingName: "Workspace Tenant Reconsent Request"
            }
        },
        ...getPostConsentRoutes().map(route => ({ ...route, $visibility: "public" as const }))
    ];
};

export const registerPrivateRoutes = (runtime: FireflyRuntime): Route[] => {
    const paths = getPaths();
    return [
        {
            path: paths.tenantAssessment,
            children: [
                {
                    element: <FullScreenTenantAssessmentAuthenticatedLayout />,
                    children: [
                        {
                            element: <NavigateToDefaultPage />,
                            index: true,
                            handle: {
                                isOnboardingStep: true
                            }
                        },
                        {
                            path: paths.tenantAssessmentMissingLicense,
                            lazy: () => import("./pages/tenant-assessment/missing-sharegate-license.tsx"),
                            handle: {
                                trackingName: "Missing ShareGate License",
                                pageTitle: "Missing ShareGate License",
                                isOnboardingStep: true
                            }
                        },
                        {
                            path: paths.tenantAssessmentNoAccess,
                            lazy: () => import("./pages/tenant-assessment/no-sg-workspace-protect-access.tsx"),
                            handle: {
                                trackingName: "Workspace Access",
                                pageTitle: "Workspace Access",
                                isRestrictedState: true
                            }
                        }
                    ]
                },
                {
                    element: <SetupRouteLayout />,
                    handle: {
                        isSetupFlow: true
                    },
                    children: [
                        {
                            path: paths.setup,
                            lazy: () => import("./pages/setup/setup.tsx"),
                            handle: {
                                trackingName: "Setup",
                                isOnboardingStep: true
                            }
                        },
                        {
                            path: paths.setupSourceTenant,
                            lazy: () => import("./pages/setup/source-tenant.tsx"),
                            handle: {
                                trackingName: "Setup Source Tenant",
                                pageTitle: "Add Your Source Tenant",
                                isOnboardingStep: true
                            }
                        },
                        {
                            path: paths.setupDataRegion,
                            loader({ params }) {
                                if (!runtime.getFeatureFlag(FeatureFlagKeys.InlineDataRegionSelectionReleased, false)) {
                                    return null;
                                }

                                return replace(
                                    generatePath(paths.setupReviewPermissions, {
                                        workspaceId: params.workspaceId ?? ""
                                    })
                                );
                            },
                            lazy: () => import("./pages/setup/data-region.tsx"),
                            handle: {
                                trackingName: "Choose Your Data Region",
                                pageTitle: "Choose Your Data Region",
                                isOnboardingStep: true
                            }
                        },
                        {
                            path: paths.setupReviewPermissions,
                            lazy: () => import("./pages/setup/review-permissions.tsx"),
                            handle: {
                                trackingName: "Review Permissions",
                                pageTitle: "Review Permissions",
                                isOnboardingStep: true
                            }
                        },
                        {
                            path: paths.setupConnected,
                            lazy: () => import("./pages/setup/connected.tsx"),
                            handle: {
                                trackingName: "Connected",
                                pageTitle: "Connected",
                                isOnboardingStep: true
                            }
                        }
                    ]
                }
            ]
        },
        ...getRemediationConsent(),
        {
            element: <TenantAssessmentAuthenticatedLayout />,
            children: [
                ...getInsightsRoutes(),
                ...getOverviewRoutes(),
                ...getRecommendationsRoutes(),
                ...getReportRoutes(runtime),
                ...getAutomationRoutes(),
                ...getReviewsRoutes(),
                ...getSensitivityLabelsRoutes(),
                ...getActivityLogRoutes(),
                ...getWorkspaceDetailsRoutes(),
                ...getUserDetailsRoutes()
            ]
        },
        {
            element: <ReviewCreationWizardLayout />,
            children: [
                {
                    handle: {
                        zeroStatePath: workspacePaths.reviewsZeroState,
                        restrictedStatePath: workspacePaths.reviewsRestrictedState
                    },
                    children: [
                        {
                            path: paths.reviewsUpdate,
                            lazy: () => import("./pages/reviews/update.[reviewId].tsx"),
                            handle: {
                                trackingName: "Reviews Edit Draft",
                                pageTitle: "Edit Review Draft"
                            }
                        }
                    ]
                }
            ]
        },
        ...redirectRoutes(runtime)
    ];
};
